<template>
  <ScrollArea
    ref="sentTemplatesScrollRef"
    @bottom="nextPage" >
    <div class="flex column">
      <div
        v-if="accessDenied"
        class="settings--limit-width" >
        <h1 class="settings__title">
          Organisation members
        </h1>

        <Forbidden />
      </div>


      <div
        v-else
        class="settings--limit-width">
        <h1 class="settings__title">
          Organisation members
        </h1>

        <Spinner
          v-if="!initialMembersRequestFinished"
          class="mt-5"
          :size="20"
          :thickness="2" />

        <!-- Actions bar -->
        <div
          v-if="initialMembersRequestFinished && (members.length || searchQuery || loadingMembers)"
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search members..."
              @input="searchMembers" />

            <!-- *************************************** -->
            <!-- Tag filtering -->
            <!-- *************************************** -->
            <!-- button -->
            <div class="sb2-wrapper mr-2 greedy">
              <!-- <button
                class="filter-button sb2-bordered sb2-hoverable sb2-focusable"
                @click="filterMenuToggle">
                <span class="filter-button-main-content">
                  <span class="sb2-icon filter size-12" />
                  Filter
                </span>
                <span
                  v-if="selectedFilters.length"
                  class="filter-button-filter-count">
                  {{ selectedFilters.length }}
                </span>
              </button> -->
              <!-- menu -->
              <div
                v-if="filterMenuIsOpen"
                v-click-outside="filterMenuClose"
                class="sb2-dropdown highlight-checked">
                <searchable-list
                  title="Filters"
                  :item-list="filterOptions"
                  :filter-fn="tagFilterSearchFn"
                  :clear-all-displayed="selectedFilters.length > 0"
                  @item-selected="selectFilter"
                  @clear-all="deselectFilter">
                  <template #default="slotProps">
                    <div
                      class="sb2-dropdown-item"
                      :class="{checked: selectedFilters.findIndex(filter => filter.value === slotProps.item.value) >= 0}">
                      <span
                        class="title-wrap ellipsis"
                        :inner-html.prop="slotProps.item.name | boldMatch(slotProps.searchTerm)" />
                      <SBIcon
                        v-if="selectedFilters.findIndex(filter => filter.value === slotProps.item.value) >= 0"
                        :icon="SBIconList.DONE_TICK"
                        :color="SBColors.bluePrimary"
                        :size="9" />
                    </div>
                  </template>
                </searchable-list>
              </div>
            </div>
            <!-- *************************************** -->

            <b-button
              class="flex center-all"
              @click="openAddMemberModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>
          </div>
        </div>

        <!-- Loading data spinner - this is a fallback for situation when data grid is hidden-->
        <div
          v-if="initialMembersRequestFinished && loadingMembers"
          class="flex center-all no-data-spinner-wrapper">
          <Spinner
            class="no-data-spinner"
            :size="20"
            :thickness="2" />
        </div>

        <!-- No data info -->
        <div
          v-if="initialMembersRequestFinished && !members.length && searchQuery && !loadingMembers"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn’t find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <div
          v-if="initialMembersRequestFinished && !members.length && !searchQuery && !loadingMembers"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>No data is available at the moment</p>
          </span>
        </div>

        <!-- <span
          v-if="loadingMembers"
          class="full-width table-count">
          <Spinner
            :size="20"
            :thickness="2" />
        </span> -->

        <!-- Data grid -->
        <div
          v-if="initialMembersRequestFinished && members.length"
          ref="tableGrid"
          class="table__grid clickable bottom-40">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Email</span>
          <span class="column-name sticky-top sticky-second">Role</span>
          <span class="column-name sticky-top sticky-second">Numbers</span>
          <span class="column-name sticky-top sticky-second">Joined at</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="members.length"
            class="full-width table-count">MEMBERS ({{ meta && meta.total ? meta.total : '-' }})</span>

          <template
            v-for="(member, index) in (members)" >
            <!-- Name column cell -->
            <div
              :key="`${index}-status`"
              :class="[`hover-${index}`, 'flex', 'flex-align-center', {'muted': member.expires_at || !member.name}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <SBAvatar
                class="no-shrink avatar mr-2"
                size="s-button"
                :show-data="true"
                :show-client="true"
                :character-count="1"
                :person="member"
                :hide-avatar-image="true" />
              <p
                class="ellipsis"
                :inner-html.prop="(member.expires_at || !member.name ? 'No name' : member.name) | boldMatch(searchQuery)" />
            </div>

            <!-- Email column cell -->
            <div
              :key="`${index}-url`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <p
                class="ellipsis"
                :inner-html.prop="member.email | boldMatch(searchQuery)" />
            </div>

            <!-- Role column cell -->
            <div
              :key="`${index}-role`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <span
                class="settings__pill"
                :class="roleClass(member)" >
                {{ member.expires_at ? invitedTitle(member) : roleName(member) }}
              </span>
            </div>

            <!-- Numbers column cell -->
            <div
              :key="`${index}-number`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <span
                v-if="member.businesses.length === 1"
                class="settings__pill ellipsis" >
                <VNodes :vnodes="getNumberIcon(availableNumbers.find(b => b.id === member.businesses[0].uid))" />{{ member.businesses[0].name }}
              </span>
              <span
                v-else
                class="settings__pill" >
                {{ availableNumbersIds.length === member.businesses.length || ['owner_SKIP-THIS-CHECK'].includes(member.role) ? 'All' : member.businesses.length }}
              </span>
            </div>

            <!-- Date joined column cell -->
            <div
              :key="`${index}-join`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <p
                v-if="member.date_joined"
                class="ellipsis">{{ member.date_joined | tableDate }}</p>
              <p
                v-else
                class="ellipsis muted" >Waiting to join</p>
            </div>

            <!-- Events column cell -->
            <!-- <div
              :key="`${index}-list`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" > -->
            <!-- <span
                v-if="member.selected_webhooks.length === 1"
                class="settings__pill">{{ member.selected_webhooks[0] }}</span>
              <template
                v-else>
                <span
                  :id="`popover2-${index}`"
                  class="settings__pill" >
                  {{ member.selected_webhooks.length }} events
                </span>
                <b-popover
                  :target="`popover2-${index}`"
                  triggers="hover"
                  placement="bottom" >
                  <p
                    v-for="(selectedWebhook, whIndex) in member.selected_webhooks"
                    :key="whIndex"
                    class="mt-0"
                    :class="{'mt-3': whIndex !== 0}" >
                    {{ selectedWebhook }}
                  </p>
                </b-popover>
              </template> -->
            <!-- </div> -->

            <!-- Menu column cell -->
            <div
              :key="`${index}-action`"
              :class="`hover-${index} flex flex-align-center`"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(member)" >
              <b-dropdown
                no-caret
                right
                toggle-class="settings__table-action"
                :disabled="!availableRoles.map(role => role.alias).includes(member.role) || (member.role === 'owner' && member.id !== me.id)"
                @click.stop.prevent
                @show="menuOpen[index] = true"
                @hide="controlHide($event, index)">
                <template #button-content>
                  <span
                    :id="`owner-edit-info-${member.id}`"
                    class="menu-button flex center-all"
                    :class="{active: menuOpen[index]}" />
                </template>
                <b-dropdown-item
                  v-if="!member.date_joined"
                  @click.stop.prevent="resend(member)" >Resend</b-dropdown-item>
                <b-dropdown-item
                  v-if="!member.date_joined"
                  class="copy-text"
                  :class="{copied: !copyLinkSuccess}"
                  @click.stop.prevent="preventHide" >
                  <span
                    v-clipboard="member.link"
                    @success="copyNumber"
                    @error="copyNumber(false)">{{ copyNumberText }}</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="member.date_joined && (member.role !== 'owner' || (member.role === 'owner' && member.id === me.id))"
                  @click.stop.prevent="editMember(member)">Edit</b-dropdown-item>
                <b-dropdown-item
                  v-if="member.role !== 'owner'"
                  @click.stop.prevent="deleteMember(member)">{{ member.id === userId ? 'Leave' : 'Delete' }}</b-dropdown-item>
                <b-dropdown-item
                  v-if="isOwner && member.id !== me.id"
                  @click.stop.prevent="transferOwnership(member)">Transfer ownership</b-dropdown-item>
              </b-dropdown>

              <b-popover
                v-if="!availableRoles.map(role => role.alias).includes(member.role) || (member.role === 'owner' && member.id !== me.id)"
                :target="`owner-edit-info-${member.id}`"
                boundary="window"
                triggers="hover"
                placement="top"
                custom-class="basic-popover mx-0"
                :delay="{ show: 200, hide: 0 }" >
                Owner account can't be edited.
              </b-popover>
            </div>
          </template>

          <!-- <Paging
            v-if="membersMeta.current_page < membersMeta.last_page"
            :class="'full-width'"
            :current-page="membersMeta.current_page"
            :total-pages="membersMeta.last_page"
            :load-more-mode="true"
            :loading="loadingMembers"
            @page="loadPage" /> -->
          <Spinner
            v-if="membersMeta.current_page < membersMeta.last_page"
            style="height: 38px;"
            class="my-3 full-width"
            :size="20"
            :thickness="2" />
        </div>

        <InviteTeamMembersModal
          ref="inviteTeamMemberModalRef"
          @invite-sent="reload" />

      </div>
    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
// import MemberInviteModal from './modals/MemberInviteModal';
// import MemberChangeRoleModal from './modals/MemberChangeRoleModal';
// import MemberChangeTeamModal from './modals/MemberChangeTeamModal';
// import MemberInvitationDeleteModal from './modals/MemberInvitationDeleteModal';
// import MemberDeleteModal from './modals/MemberDeleteModal';
import SearchableList from '../../common/SearchableList';
import ToasterMixin from '@/mixins/Toaster';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SearchInput from '@/components/business/widgets/SearchInput';
import Spinner from '@/components/common/Spinner';
import Paging from '@/components/common/Paging';
import InviteTeamMembersModal from '@/components/business/settings/modals/InviteTeamMembers.modal';
import { getNumberIcon } from '@/helpers/lists/branchIcons';
import ScrollArea from '@/components/common/ScrollArea';
import roles from '@/helpers/lists/roles';
import moment from '@/helpers/momentz';
import Forbidden from '@/components/errors/Forbidden';
import SBAvatar from '../../common/Avatar';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import VNodes from '@/components/common/VNodes';

export default {
  name: 'TeamMemberSettings',
  components: {
    InviteTeamMembersModal,
    Spinner,
    SearchableList,
    SearchInput,
    ScrollArea,
    SBIcon,
    Paging,
    VNodes,
    Forbidden,
    SBAvatar,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      initialMembersRequestFinished: false,
      searchQuery: '',
      selectedFilters: [],
      filterMenuIsOpen: false,
      filterOptions: [],
      menuOpen: [],
      copyLinkSuccess: false,
      resendSuccess: false,
      noHide: false,
      forbidden: false,
      searchModeActive: false,
      meta: null,
      roles,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      members: 'merchant/membersAndInvitations',
      loadingMembers: 'merchant/loadingMembers',
      membersMeta: 'merchant/membersMeta',
      currentOrganisation: 'organisation/currentOrganisation',
      currentOrganisationBusinessIds: 'organisation/currentOrganisationBusinessIds',
      organisationNumbers: 'organisation/organisationNumbers',
      me: 'organisation/me',
      userId: 'user/id',
      apiError: 'merchant/apiError',
      currentOrganisationMembers: 'organisation/currentOrganisationMembers',
      userLoaded: 'user/userLoaded',
    }),
    accessDenied() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    availableNumbers() {
      if (this.isAdmin || this.isOwner) {
        return this.organisationNumbers;
      }
      return this.currentOrganisation.businesses;
    },
    availableNumbersIds() {
      if (this.isAdmin || this.isOwner) {
        return this.organisationNumbers.map(number => number.id);
      }
      return this.currentOrganisationBusinessIds;
    },
    copyNumberText() {
      if (this.copyLinkSuccess) {
        return 'Copied!';
      }
      return 'Copy invitation link';
    },
    resendText() {
      if (this.resendSuccess) {
        return 'Sent!';
      }
      return 'Resend';
    },
    availableRoles() {
      if (this.isOwner) {
        return roles;
      }
      return roles.filter(r => r.alias !== 'owner');
    },
  },
  watch: {
    accessDenied() {
      if (!this.accessDenied && this.initialMembersRequestFinished) {
        this.fetchMembersWithInvitations();
      }
    },
    /** Members change if ownership has been transfered so we need to update the members list. */
    currentOrganisationMembers() {
      this.silentReload();
    },
    userLoaded() {
      if (this.userLoaded) {
        this.silentReload();
      }
    },
    members() {
      this.initialMembersRequestFinished = true;
      this.meta = { ...this.membersMeta }; // NOTE - total results in paging is acurate only on the first page so this is why we are only using it from initial response
      // this.loadMoreIfNeeded();
      const editMemberId = this.$refs.inviteTeamMemberModalRef.getEditingMemberId();
      if (editMemberId) {
        const targetMember = this.members.find(member => member.id === editMemberId);
        this.$refs.inviteTeamMemberModalRef.resetForm(targetMember);
      }
      if (!this.searchQuery) {
        this.searchModeActive = false;
      }
    },
    currentOrganisation(newOrg, oldOrg) {
      if (newOrg && oldOrg && newOrg.id !== oldOrg.id && !this.accessDenied && this.currentOrganisation.id && this.initialMembersRequestFinished) {
        this.fetchMembersWithInvitations();
      }
    },
  },
  mounted() {
    if (!this.accessDenied && this.currentOrganisation.id) {
      this.fetchMembersWithInvitations();
    }
    if (!this.organisationNumbers.length) {
      this.fetchOrganisationNumbers();
    }
    window.eventBus.$on('403', () => { this.forbidden = true; });
    window.eventBus.$on('contact-invitation-created', this.silentReload);
    window.eventBus.$on('user-activation-action', this.silentReload);
    this.finalizeSplashView();
  },
  beforeDestroy() {
    // TODO check if all $on's have their $off's
    window.eventBus.$off('403');
    window.eventBus.$off('contact-invitation-created');
    window.eventBus.$off('user-activation-action');
  },
  methods: {
    ...mapActions({
      fetchMembersWithInvitations: 'merchant/fetchMembersWithInvitations',
      reInviteMember: 'merchant/reInviteMember',
      deleteInvitationApi: 'merchant/deleteInvitation',
      deleteMemberApi: 'merchant/deleteMember',
      finalizeSplashView: 'organisation/finalizeSplashView',
      editMemberApi: 'merchant/editMember',
      transferOwnershipVuex: 'merchant/transferOwnership',
      fetchOrganisationMembers: 'organisation/fetchOrganisationMembers',
      fetchOrganisationNumbers: 'organisation/fetchOrganisationNumbers',
    }),
    ...mapMutations({
      updateMemberInvite: 'merchant/UPDATE_MEMBER_INVITE',
    }),
    getNumberIcon,
    businessIndex(businessesId) {
      return this.availableNumbersIds.findIndex(id => id === businessesId);
    },
    preventHide() {
      // this.noHide = true;
    },
    controlHide(event, index) {
      if (this.noHide) {
        this.noHide = false;
        event.preventDefault();
      } else {
        this.menuOpen[index] = false;
        this.copyLinkSuccess = false;
        this.resendSuccess = false;
      }
    },
    copyNumber(success = true) {
      if (success) {
        this.copyLinkSuccess = true;
        this.showToaster('Invitation link has been successfully copied.', null, null, 'OK');
      }

      // remove copy info after 3 seconds
      setTimeout(() => {
        this.copyLinkSuccess = false;
      }, 1500);
    },
    deleteMember(member) {
      const name = member.name ? member.name : member.email;
      const nameEmail = member.name ? `${member.name} (${member.email})` : member.email;

      const h = this.$createElement;
      const memberDeleteText = [
        h(
          'p',
          [
            'Are you sure you want to permanently delete member ',
            h('b', nameEmail),
            ' from ',
            h('b', this.currentOrganisation.name),
            ' organisation?',
          ],
        ),
        h(
          'p',
          { class: 'mt-3' },
          [
            'Please note, deleting this member will ',
            h('b', 'unassign'),
            ' their conversations.',
          ],
        ),
        h(
          'div',
          { class: 'modal-attention-note' },
          [
            h(
              'SBIcon',
              {
                props: {
                  icon: SBIconList.ERROR,
                  color: this.SBColors.blackPrimary,
                  size: 12,
                },
              },
            ),
            h(
              'span',
              [
                h('p', 'Removal of this license will be reflected in your next renewal.'),
                h('p', 'No credits or refunds will be given for the current billing cycle.'),
              ],
            ),
          ],
        ),
      ];
      const memberLeaveText = [
        h(
          'p',
          [
            'Are you sure you want to permanently leave ',
            h('b', this.currentOrganisation.name),
            ' organisation?',
          ],
        ),
        h(
          'p',
          { class: 'mt-3' },
          'Please note, leaving the organisation will unassign all your conversations.',
        ),
        h(
          'div',
          { class: 'modal-attention-note' },
          [
            h(
              'SBIcon',
              {
                props: {
                  icon: SBIconList.ERROR,
                  color: this.SBColors.blackPrimary,
                  size: 12,
                },
              },
            ),
            h(
              'span',
              [
                h('p', 'Removal of this license will be reflected in your next renewal.'),
                h('p', 'No credits or refunds will be given for the current billing cycle.'),
              ],
            ),
          ],
        ),
      ];

      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: member.date_joined ? (member.id === this.userId ? `Leave ${this.currentOrganisation.name}` : 'Delete member') : 'Remove invitation',
        message: member.date_joined ? (member.id === this.userId ? memberLeaveText : memberDeleteText) : `Are you sure you want to delete invitation for <b>${member.email}<b>?`,
        confirmButtonText: (member.id === this.userId ? 'Leave' : 'Delete'),
        showUndoWarning: false,
        autoClose: false,
        confirmCallback: () => {
          if (member.date_joined) {
            this.deleteMemberApi({ memberId: member.id, data: { includeMe: false } })
              .then(() => {
                if (member.id === this.userId) {
                  this.showToaster(`You left the ${this.currentOrganisation.name} organisation.`, null, null, 'OK');
                } else {
                  const name = member.name ? `${member.name} (${member.email})` : member.email;
                  this.showToaster(`<b>${name}</b> has been successfully deleted.`, null, null, 'OK');
                }

                this.removeMember(member);
                // TODO - Test this with Ivan and also make sure redirection org route works and the "no organisation" message is shown if user is not a member of any organisation.
                if (member.id === this.userId) {
                  this.$router.push({ name: 'Organisation' });
                }
                this.$confirmPluginModal.close();
              });
          } else {
            this.deleteInvitationApi({ invitationId: member.id, data: { includeMe: false } })
              .then(() => {
                this.removeMember(member);
                this.showToaster(`Invitation for <b>${member.email}</b> has been successfully deleted`, null, null, 'OK');
                this.$confirmPluginModal.close();
              });
          }
        },
      });
    },
    transferOwnership(member) {
      const nameEmail = member.name ? `${member.name} (${member.email})` : member.email;
      const h = this.$createElement;
      const transferText = [
        h(
          'p',
          [
            'Are you sure you want to permanently transfer ownership of this organization to ',
            h('b', nameEmail),
            '?',
          ],
        ),
        h(
          'div',
          { class: 'modal-attention-note' },
          [
            h(
              'SBIcon',
              {
                props: {
                  icon: SBIconList.ERROR,
                  color: this.SBColors.blackPrimary,
                  size: 12,
                },
              },
            ),
            h(
              'span',
              [
                h('p', 'Please note that transferring ownership to this member will change your role to Admin.'),
              ],
            ),
          ],
        ),
      ];

      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Transfer organisation ownership',
        message: transferText,
        confirmButtonText: 'Confirm',
        showUndoWarning: true,
        autoClose: 'NO',
        confirmCallback: () => {
          const payload = {
            new_owner_user_id: member.id,
          };


          this.transferOwnershipVuex(payload)
            .then((response) => {
              if (response.status === 200) {
                this.$confirmPluginModal.close();
                this.fetchOrganisationMembers(this.currentOrganisation.id);
              }
            })
            .catch((error) => {
              this.showToaster('There was an error while transfering ownership. Please try again later.', null, null, 'error');
              this.$confirmPluginModal.finishWorking();
            });
        },
      });
    },
    removeMember(memberToDelete) {
      const index = this.members.findIndex(member => member.id === memberToDelete.id);
      this.members.splice(index, 1);
    },
    editMember(member) {
      this.$refs.inviteTeamMemberModalRef.show(member);
    },
    resend(member) {
      // this.noHide = true;
      this.resendSuccess = true;

      this.reInviteMember({ inivitationId: member.id })
        .then((response) => {
          if (response.status === 200) {
            this.updateMemberInvite({
              targetMemberId: member.id,
              newData: {
                link: response.data.invitation_link,
                expiresAt: response.data.expires_at,
              },
            });
            this.showToaster(`${member.email} has been successfully reinvited.`, null, null, 'OK');
          }
        })
        .catch(() => {
          this.showToaster(`There was an error while resending an invitation to ${member.email}. Please try again.`, null, null, 'error');
        });
    },
    searchMembers() {
      this.fetchMembersWithInvitations({
        data: {
          page: 1,
          search_query: this.searchQuery,
        },
      });
    },
    filterMenuToggle() {},
    filterMenuClose() {},
    tagFilterSearchFn() {},
    selectFilter() {},
    deselectFilter() {},
    openAddMemberModal() {
      this.$refs.inviteTeamMemberModalRef.show();
    },
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    nextPage() {
      if (this.membersMeta.current_page >= this.membersMeta.last_page) {
        return;
      }
      this.loadPage(this.membersMeta.current_page + 1);
    },
    loadPage(page) {
      this.fetchMembersWithInvitations({
        data: {
          page,
        },
        append: true,
        skipInvitations: true,
      });
    },
    silentReload() {
      // console.log('SILENT RELOAD');
      this.fetchMembersWithInvitations({
        data: {
          page: 1,
          search_query: this.searchQuery,
        },
        silentReload: true,
      });
    },
    loadMoreIfNeeded() {
      if (!this.$refs.sentTemplatesScrollRef.verticalScrollActive) {
        this.nextPage();
      }
    },
    selectRow(member) {
      if (member.date_joined) { // joined user
        if (this.availableRoles.filter(r => r.alias !== 'owner').map(role => role.alias).includes(member.role) || (this.isOwner && member.id === this.me.id)) {
          this.editMember(member);
        } else { // catch owner role here
          this.showToaster('Owner account can\'t be edited.', null, null, 'warning');
        }
      } else if (this.$moment(member.expires_at) < this.$moment()) { // expired invitation
        this.showToaster(`Invitation for <b>${member.email}</b> expired on <b>${moment(member.expires_at).format('D MMMM')}</b> at <b>${moment(member.expires_at).format('HH:mm')}</b>`, null, null, 'error');
      } else { // invitation
        this.showToaster(`Invitation for <b>${member.email}</b> will expire on <b>${moment(member.expires_at).format('D MMMM')}</b> at <b>${moment(member.expires_at).format('HH:mm')}</b>`, null, null, 'error');
      }
    },
    invitedTitle(member) {
      let title = 'Invited';
      if (this.$moment(member.expires_at) < this.$moment()) {
        title = 'Invitation expired';
      }
      return title;
    },
    reload() {
      this.fetchMembersWithInvitations();
    },
    roleClass(member) {
      let roleClassValue = '';
      if (!member.expires_at) {
        roleClassValue = 'settings__pill--blue';
      } else if (this.$moment(member.expires_at) < this.$moment()) {
        roleClassValue = 'settings__pill--red';
      }

      return roleClassValue;
    },
    roleName(member) {
      let role;
      if (member.role === 'owner') {
        role = 'Owner';
      } else {
        role = this.availableRoles.find(role => role.alias === member.role);
        role = role ? role.name : '';
      }
      return role;
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.actions-bar {
  margin-top: 35px;
  margin-bottom: 15px;
}

.table__grid {
  grid-template-columns: minmax(50px, auto) minmax(50px, auto) auto minmax(0, auto) auto min-content !important;

  .full-width {
    grid-column: 1 / 7 !important;
  }
}

.branch-icon {
  margin-right: 5px;
  display: inline !important;
  font-size: 10px !important;

  &.integration {
    width: 14px;
    height: 14px;
    display: inline-block !important;
    transform: translateY(1px);
  }
}

::v-deep .copy-text {
  a.dropdown-item {
    padding: 0 !important;

    span {
      display: block;
      padding: 10px 15px;
    }
  }
}
</style>
